import { Navigate } from "react-router-dom";
import { useIsPendingDriver } from "@/stores/authStore";

interface RequireApprovedDriverProps {
  children: React.ReactNode;
}

/**
 * Route guard for driver-only account pages (rides, availability, driver/*).
 *
 * Pending drivers are redirected to the pending-approval status page instead
 * of landing on silently-empty driver pages (their solo company and driver
 * profile are only created at approval time, so the API has nothing to serve).
 *
 * This is a UX guard only — the API independently enforces access:
 * vehicles routes 403 without a solo-driver company, availability routes 404
 * without a driver profile, and reservation queries are owner-scoped.
 *
 * @param props - The component props.
 * @param props.children - The driver page to render when not pending.
 * @returns The guarded page or a redirect to /account/pending-approval.
 */
export function RequireApprovedDriver({
  children,
}: RequireApprovedDriverProps): React.ReactElement {
  const isPendingDriver = useIsPendingDriver();

  if (isPendingDriver) {
    return <Navigate to="/account/pending-approval" replace />;
  }

  return <>{children}</>;
}
